'use client'

import { useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'

import type { WorkspaceMemberRole } from '@modules/settings'
import { runInviteMemberWithToasts } from '@modules/settings/utils/runInviteMemberWithToasts'
import type { InviteMemberCallbacks } from '@modules/settings/workspace/stores/workspace.type'
import { useToastStore } from '@stores/toast/toast.store'

type UseLayoutInviteHandlersParams = {
  inviteProjectMember: (email: string, role: WorkspaceMemberRole, callbacks?: InviteMemberCallbacks) => Promise<void>
  inviteWorkspaceMember: (email: string, role: WorkspaceMemberRole, callbacks?: InviteMemberCallbacks) => Promise<void>
}

type InviteDialogKey = 'inviteToProjectDialog' | 'inviteToWorkspaceDialog'

export function useLayoutInviteHandlers({ inviteProjectMember, inviteWorkspaceMember }: UseLayoutInviteHandlersParams) {
  const t = useTranslations('core')
  const showToast = useToastStore((state) => state.showToast)

  const projectConflictMessages = useMemo(
    () => ({
      alreadyMember: t('inviteToProjectDialog.conflictAlreadyMember'),
      alreadyInvited: t('inviteToProjectDialog.conflictAlreadyInvited'),
    }),
    [t]
  )

  const workspaceConflictMessages = useMemo(
    () => ({
      alreadyMember: t('inviteToWorkspaceDialog.conflictAlreadyMember'),
      alreadyInvited: t('inviteToWorkspaceDialog.conflictAlreadyInvited'),
    }),
    [t]
  )

  const runInvite = useCallback(
    (
      email: string,
      role: WorkspaceMemberRole,
      inviteFn: UseLayoutInviteHandlersParams['inviteProjectMember'],
      dialogKey: InviteDialogKey,
      conflictMessages: { alreadyInvited: string; alreadyMember: string }
    ) =>
      runInviteMemberWithToasts(inviteFn, email, role, {
        conflictMessages,
        showToast,
        toast: {
          successTitle: t(`${dialogKey}.toastSuccessTitle`),
          successDescription: t(`${dialogKey}.toastSuccessDescription`, { email }),
          errorTitle: t(`${dialogKey}.toastErrorTitle`),
          informationalDescription: t(`${dialogKey}.toastAlreadyInvitedDescription`),
        },
      }),
    [showToast, t]
  )

  const handleInviteProject = useCallback(
    (email: string, role: WorkspaceMemberRole) =>
      runInvite(email, role, inviteProjectMember, 'inviteToProjectDialog', projectConflictMessages),
    [inviteProjectMember, projectConflictMessages, runInvite]
  )

  const handleInviteWorkspace = useCallback(
    (email: string, role: WorkspaceMemberRole) =>
      runInvite(email, role, inviteWorkspaceMember, 'inviteToWorkspaceDialog', workspaceConflictMessages),
    [inviteWorkspaceMember, runInvite, workspaceConflictMessages]
  )

  return { handleInviteProject, handleInviteWorkspace }
}
